import React, { TouchEventHandler, useCallback, useEffect, useMemo, useState } from 'react';

import { debounce, uniqBy } from 'lodash-es';

import { ILocation } from '@rbi-ctg/frontend';
import { IRestaurant } from '@rbi-ctg/store';
import useMap from 'hooks/use-map';
import { IUseMapArgs } from 'hooks/use-map/types';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { useEnableMapListExperiment } from 'pages/store-locator/use-enable-map-list-experiment';
import { ClickEventComponentNames, CustomEventNames, useMParticleContext } from 'state/mParticle';
import { useStoreContext } from 'state/store';

import useMarkers from '../../hooks/use-markers';
import { StoreCard } from '../store-card';

import { MapContainer } from './map.view.styled';

import { StoreLocatorMap } from '.';

export interface IMapViewProps {
  activeCoordinates: ILocation | null;
  currentViewIsMap: boolean;
  handleResetLocation: () => void;
  onPressSearch: (location: ILocation) => void;
  storesFavs: IRestaurant[];
  storesRecent: IRestaurant[];
  storesNearby: IRestaurant[];
  isInSupportForm?: boolean;
  selectStoreFormField?: (storeId: string) => void;
  onSelectRestaurant?: (storeId?: string) => void;
}

const setupEvtListeners = (onDragEnd: TouchEventHandler) => ({
  dragend: debounce(onDragEnd, 200),
});

const MapViewStoreCard: React.FC<{
  restaurant: IRestaurant;
  isSelected: boolean;
  selectStoreFormField?: (storeId: string) => void;
  isInSupportForm?: boolean;
}> = ({ restaurant, isSelected, selectStoreFormField, isInSupportForm }) => {
  return (
    <>
      <StoreCard
        restaurant={restaurant}
        isSelected={isSelected}
        selectStoreFormField={selectStoreFormField}
        isInMapView
        isInSupportForm={isInSupportForm}
      />
    </>
  );
};

export const MapView: React.FC<React.PropsWithChildren<IMapViewProps>> = ({
  activeCoordinates,
  currentViewIsMap,
  handleResetLocation,
  onPressSearch,
  storesFavs,
  storesNearby,
  storesRecent,
  isInSupportForm,
  selectStoreFormField,
  onSelectRestaurant,
}) => {
  const { store } = useStoreContext();

  const isDesktop = useIsDesktopBreakpoint();
  const { logButtonClick } = useMParticleContext();
  const enableMapListExperiment = useEnableMapListExperiment();
  const [selectedStore, setSelectedStore] = useState<IRestaurant | null>(null);
  const [hasPanned, setHasPanned] = useState(false);

  const eventListeners: IUseMapArgs['eventListeners'] = useMemo(() => {
    return {
      ...setupEvtListeners(() => {
        setHasPanned(true);
      }),
      onPress: () => setSelectedStore(null),
    };
  }, []);

  const { center, createMarker, map, zoom, panTo } = useMap(
    activeCoordinates
      ? {
          eventListeners,
          position: activeCoordinates,
        }
      : { eventListeners }
  );

  const onPressButtonSearch = useCallback(() => {
    setHasPanned(false);

    onPressSearch(center);
  }, [center, onPressSearch]);

  /**
   * TODO:
   *  - should we still include favs & recents in map view, if we have values?
   *  - added recents here bc idk why they weren't there in current implementation
   */
  const storesWithMarkers = useMemo(
    () => uniqBy([...storesNearby, ...storesFavs, ...storesRecent], '_id'),
    [storesFavs, storesNearby, storesRecent]
  );

  const onPress = useCallback(
    (store: IRestaurant) => {
      if (!store._id) {
        return;
      }

      setSelectedStore(prevSelectedStore => {
        if (prevSelectedStore !== store) {
          logButtonClick({
            attributes: {
              ...(store._id && { StoreId: store._id }),
              Name: CustomEventNames.BUTTON_CLICK_STORE_SELECTED,
              name: CustomEventNames.BUTTON_CLICK_STORE_SELECTED,
              component: ClickEventComponentNames.STORE_LOCATOR_MAP_MARKER,
            },
          });
        }
        enableMapListExperiment &&
          onSelectRestaurant &&
          onSelectRestaurant(store.storeId || undefined);
        return store;
      });

      // TODO: don't think we need this - just log the marker click
      // const { latitude: lat, longitude: lng } = store;

      // if (lat && lng) {
      //   return searchNearbyRestaurants({ location: { lat, lng } });
      // }
    },
    [logButtonClick]
  );

  useMarkers({
    createMarker,
    storesFavs,
    storesNearby: storesWithMarkers,
    activeStoreId: selectedStore?._id,
    onPress,
    panTo,
  });

  const isSelected = store._id === selectedStore?._id;

  useEffect(() => setSelectedStore(!store._id ? null : store), [store]);

  return (
    <MapContainer enableMapListExperiment={enableMapListExperiment && !currentViewIsMap}>
      <StoreLocatorMap
        map={map}
        currentViewIsMap={currentViewIsMap || enableMapListExperiment}
        handleResetLocation={handleResetLocation}
        hasPanned={hasPanned}
        isDesktop={isDesktop}
        onPressSearch={onPressButtonSearch}
        zoom={zoom}
      />
      {currentViewIsMap && selectedStore && (
        <MapViewStoreCard
          restaurant={selectedStore}
          isSelected={isSelected}
          selectStoreFormField={selectStoreFormField}
          isInSupportForm={isInSupportForm}
        />
      )}
    </MapContainer>
  );
};
